import { useRef, useState } from "react";
import { GripVertical, Palette, Trash2, CheckSquare, StickyNote, AlarmClock, MessageCircle, Bookmark, Radio, Shapes } from "lucide-react";
import { PALETTES, paletteStyle } from "@/lib/palettes";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TaskCard from "@/components/cards/TaskCard";
import NoteCard from "@/components/cards/NoteCard";
import ReminderCard from "@/components/cards/ReminderCard";
import ChatCard from "@/components/cards/ChatCard";
import BookmarkCard from "@/components/cards/BookmarkCard";
import PingCard from "@/components/cards/PingCard";
import CustomCard from "@/components/cards/CustomCard";
import WhatsAppCard, { WhatsAppIcon } from "@/components/cards/WhatsAppCard";
import ComplimentCard from "@/components/cards/ComplimentCard";
import MotivationCard from "@/components/cards/MotivationCard";
import LearningCard from "@/components/cards/LearningCard";
import NewsCard from "@/components/cards/NewsCard";
import ContentCard from "@/components/cards/ContentCard";
import { Heart, Flame, Lightbulb, Newspaper, Rss } from "lucide-react";

const META = {
  task: { icon: CheckSquare, label: "Task", C: TaskCard },
  note: { icon: StickyNote, label: "Note", C: NoteCard },
  reminder: { icon: AlarmClock, label: "Reminder", C: ReminderCard },
  chat: { icon: MessageCircle, label: "Chat", C: ChatCard },
  bookmark: { icon: Bookmark, label: "Bookmarks", C: BookmarkCard },
  ping: { icon: Radio, label: "Ping", C: PingCard },
  whatsapp: { icon: WhatsAppIcon, label: "WhatsApp", C: WhatsAppCard },
  compliment: { icon: Heart, label: "Compliments", C: ComplimentCard },
  motivation: { icon: Flame, label: "Motivation", C: MotivationCard },
  learning: { icon: Lightbulb, label: "Learning", C: LearningCard },
  news: { icon: Newspaper, label: "News", C: NewsCard },
  content: { icon: Rss, label: "Content", C: ContentCard },
  custom: { icon: Shapes, label: "Custom", C: CustomCard },
};

export default function CardShell({ card, zoom, dark, users, me, online, onUpdate, onDelete, gridMode, connectMode, connectSource, onConnectClick, onDragMove, registerRef }) {
  const meta = META[card.type] || META.custom;
  const [pos, setPos] = useState(null);
  const [size, setSize] = useState(null);
  const dragRef = useRef(null);
  const style = paletteStyle(card.color, dark);
  const x = pos ? pos.x : card.x;
  const y = pos ? pos.y : card.y;
  const w = size ? size.w : card.w;
  const h = size ? size.h : card.h;

  const startDrag = (e) => {
    if (gridMode || connectMode) return;
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: card.x, oy: card.y, moved: false };
    document.body.classList.add("dragging");
    const move = (ev) => {
      const d = dragRef.current;
      d.moved = true;
      const nx = d.ox + (ev.clientX - d.sx) / zoom;
      const ny = d.oy + (ev.clientY - d.sy) / zoom;
      setPos({ x: nx, y: ny });
      onDragMove?.(card._id, { x: nx, y: ny });
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.classList.remove("dragging");
      const d = dragRef.current;
      if (d.moved) {
        const nx = d.ox + (ev.clientX - d.sx) / zoom;
        const ny = d.oy + (ev.clientY - d.sy) / zoom;
        onUpdate(card._id, { x: nx, y: ny });
      }
      onDragMove?.(card._id, null);
      setPos(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const clampSize = (ev, start) => ({
    w: Math.min(800, Math.max(240, start.ow + (ev.clientX - start.sx) / zoom)),
    h: Math.min(900, Math.max(120, start.oh + (ev.clientY - start.sy) / zoom)),
  });

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget.parentElement;
    const start = { sx: e.clientX, sy: e.clientY, ow: card.w, oh: card.h || el.offsetHeight };
    document.body.classList.add("dragging");
    const move = (ev) => setSize(clampSize(ev, start));
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.classList.remove("dragging");
      onUpdate(card._id, clampSize(ev, start));
      setSize(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const Body = meta.C;
  const assignedToMe = card.type === "task" && card.data?.assignee_id === me?.id && card.owner_id !== me?.id;

  return (
    <div
      ref={(el) => registerRef?.(card._id, el)}
      data-testid={`${card.type}-card`}
      onClickCapture={connectMode ? (e) => { e.stopPropagation(); e.preventDefault(); onConnectClick?.(card._id); } : undefined}
      className={`card-pop select-none rounded-2xl border-2 shadow-lg transition-shadow hover:shadow-xl ${
        gridMode ? "relative mb-4 w-full break-inside-avoid" : "absolute"
      } ${connectMode ? "cursor-crosshair" : ""} ${connectSource === card._id ? "ring-4 ring-pink-500/70" : ""}`}
      style={gridMode ? { ...style } : { left: x, top: y, width: w, minHeight: h || undefined, ...style }}
    >
      <div
        className="flex cursor-grab items-center gap-1.5 rounded-t-2xl px-3 py-2 active:cursor-grabbing"
        onPointerDown={startDrag}
        data-testid={`${card.type}-card-drag-handle`}
        style={{ borderBottom: `1px dashed ${style.borderColor}55` }}
      >
        <GripVertical className="h-4 w-4 opacity-40" />
        <meta.icon className="h-4 w-4" />
        <span className="font-display text-sm font-bold tracking-tight">{meta.label}</span>
        {assignedToMe && (
          <span className="ml-1 rounded-full bg-white/50 px-2 py-0.5 text-[10px] font-mono uppercase" title={`Assigned by ${card.owner_name}`}>
            from {card.owner_name?.split(" ")[0]}
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5" onPointerDown={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`${card.type}-card-color-button`} className="rounded-lg p-1 opacity-60 transition-opacity hover:opacity-100">
                <Palette className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl bg-popover/95 backdrop-blur-xl">
              <div className="grid grid-cols-4 gap-1.5 p-1.5">
                {Object.entries(PALETTES).map(([id, p]) => (
                  <button key={id} data-testid={`color-swatch-${id}`} title={p.name}
                    onClick={() => onUpdate(card._id, { color: id })}
                    className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${card.color === id ? "ring-2 ring-violet-500 ring-offset-1" : ""}`}
                    style={{ background: dark ? p.darkBg : p.bg, borderColor: p.border }} />
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid={`${card.type}-card-delete-button`} onClick={() => onDelete(card._id)}
            className="rounded-lg p-1 opacity-60 transition-opacity hover:opacity-100 hover:text-red-500">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-3 pb-3 pt-2" onPointerDown={(e) => e.stopPropagation()}>
        <Body card={card} users={users} me={me} online={online} dark={dark} style={style}
          onData={(data) => onUpdate(card._id, { data })} />
      </div>
      {!gridMode && (
        <div
          data-testid={`${card.type}-card-resize-handle`}
          onPointerDown={startResize}
          className="absolute bottom-0 right-0 h-5 w-5 cursor-nwse-resize rounded-br-xl opacity-50 transition-opacity hover:opacity-100"
          style={{ borderRight: `3px solid ${style.borderColor}`, borderBottom: `3px solid ${style.borderColor}` }}
          title="Resize"
        />
      )}
    </div>
  );
}
